import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { canAccessPage } from '../../utils/permissions';
import { FaUserShield, FaClock, FaExclamationTriangle } from 'react-icons/fa';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPage?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredPage }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // pending 상태는 제거 - 모든 인증된 사용자는 기본적으로 접근 가능

  // 특정 페이지 접근 권한 확인
  if (requiredPage && !canAccessPage(user, requiredPage)) {
    return <AccessDeniedPage requiredPage={requiredPage} />;
  }

  return <>{children}</>;
};

// 승인 대기 페이지 컴포넌트
const PendingApprovalPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <FaClock className="h-16 w-16 text-blue-500" />
              <div className="absolute -top-1 -right-1 h-6 w-6 bg-yellow-500 rounded-full flex items-center justify-center">
                <FaExclamationTriangle className="h-3 w-3 text-white" />
              </div>
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            권한 승인 대기 중
          </h1>
          
          <div className="space-y-4 text-gray-600 dark:text-gray-300">
            <p className="text-lg">
              안녕하세요!
            </p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <p className="text-sm">
                현재 계정이 관리자 승인을 기다리고 있습니다.
                <br />
                승인이 완료되면 시스템을 이용하실 수 있습니다.
              </p>
            </div>
            
            <div className="border-t dark:border-gray-700 pt-4">
              <p className="text-sm font-medium mb-2">승인 문의:</p>
              <div className="text-sm space-y-1">
                <p>📧 이메일: admin@bfgc.org</p>
                <p>📞 전화: 교회 사무실</p>
              </div>
            </div>
          </div>
          
          <div className="mt-8">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
            >
              새로고침
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 접근 거부 페이지 컴포넌트
const AccessDeniedPage: React.FC<{ requiredPage: string }> = ({ requiredPage }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <FaUserShield className="h-16 w-16 text-red-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            접근 권한이 없습니다
          </h1>
          
          <div className="space-y-4 text-gray-600 dark:text-gray-300">
            <p>
              죄송합니다. 현재 권한으로는 이 페이지에 접근할 수 없습니다.
            </p>
            
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
              <p className="text-sm">
                <span className="font-semibold">요청된 페이지:</span> {requiredPage}
                <br />
                <span className="font-semibold">현재 권한:</span> 승인 대기
              </p>
            </div>
            
            <p className="text-sm">
              필요한 권한이 있다고 생각되시면 관리자에게 문의해주세요.
            </p>
          </div>
          
          <div className="mt-8 space-y-3">
            <button
              onClick={() => window.history.back()}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              이전 페이지로
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              홈으로 이동
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};